import { message } from "antd";
import type { CSSProperties } from "react";
import { AxiosError } from "axios";
import { Dayjs } from "dayjs";
import { translate, translateWithVars } from "shared/languages";
import { CustomerPackage, InactiveReason, User } from "shared/types";
import { DiscountCodeEffectiveStatus } from "shared/types/discountCode";
import { ResponseHandler } from "shared/types/axios";
import {
  BookingStatus,
  CommissionMode,
  CommissionRateStructure,
  PackageSystemKind,
  PayrollRunStatus,
  ReferralEffectiveStatus,
  ReferralStatus,
  ScheduleTimeStatus,
  UserType,
  ValidityStart,
  VoucherStatus,
} from "shared/types/enum";
import { localTzDate, now, tzDate } from "./dayjs";
import { colors } from "shared/theme/color";

export const isUUID = (value: string) =>
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value);

export const getApiSuccessMessage = (successMessage: string) => {
  message.success(successMessage[0].toUpperCase() + successMessage.slice(1));
};

export const formatDateNoTimeToLocalString = (date: string | Date | Dayjs) => {
  return localTzDate(date).format("dddd, DD/MM/YYYY");
};

export const formatDateToLocalString = (date: string | Date | Dayjs) => {
  return localTzDate(date).format("dddd, DD/MM/YYYY HH:mm");
};

// Buckets items with an ISO occurrence_timestamp by their Jakarta calendar
// date, sorted chronologically both across days and within a day — shared by
// the leave-conflict and employee-management-reactivation warning modals so
// both render the identical day-grouped layout from one correct
// implementation instead of two hand-copied ones.
export const groupByDate = <T extends { occurrence_timestamp: string }>(
  items: T[],
): [string, T[]][] => {
  const byDate = new Map<string, T[]>();
  [...items]
    .sort(
      (a, b) =>
        new Date(a.occurrence_timestamp).getTime() -
        new Date(b.occurrence_timestamp).getTime(),
    )
    .forEach((item) => {
      const dateKey = localTzDate(item.occurrence_timestamp).format("YYYY-MM-DD");
      byDate.set(dateKey, [...(byDate.get(dateKey) ?? []), item]);
    });
  return Array.from(byDate.entries()).sort(([a], [b]) => a.localeCompare(b));
};

// Phone display lives in shared/utils/phone.ts (formatPhone). The old
// formatPhoneNumberWithCountryCode just glued a hardcoded "+62" onto a bare
// local number; numbers are stored as E.164 now, so that would double the
// country code and would be wrong for every non-Indonesian number.

export const translateMessage = (message: string): string => {
  return message.replace(/translate\('([^']+)'\)/g, (_, key) => translate(key));
};

export const translateApiErrorMessage = (error: AxiosError<ResponseHandler>) => {
  let errorDescription: string = error.response?.data?.error || `translate('messages.error.general')`;
  const msg = errorDescription.replace(/translate\('([^']+)'\)/g, (_, key) => translate(key));
  message.error(msg);
};

// This function parses the error message from the backend API
// Returns a default notification message of 'something went wrong'
// TODO: Modify this function to handle different error messages based on error code
// TODO: Modify to handle different error message in different language and transfer to the method above!
export const getApiErrorMessage = (error: AxiosError<ResponseHandler>) => {
  // Surface the server's human message when present (createResponse puts it on
  // `message` or `error`), so reasons like "Waitlist is full." actually reach
  // the admin instead of a generic toast. Fall back to the generic message.
  const data = error.response?.data;
  const serverMessage =
    (typeof data?.message === "string" && data.message) ||
    (typeof data?.error === "string" && data.error) ||
    undefined;
  // Resolve translate('key') markers (the server's localizable errors, e.g.
  // a Pembatasan Admin 403) in the admin's language; plain strings pass
  // through translateMessage unchanged.
  message.error(
    serverMessage
      ? translateMessage(serverMessage)
      : translate("messages.error.general"),
  );
};

// Whether a user is deactivated for the given role context. Customers are
// per-studio: their active flag lives on customer_business.deleted_at, which the
// server's getUserById surfaces onto customer.deleted_at for the acting studio.
export const isUserDeactivated = (user: User, checkUserType: UserType) => {
  if (user.deleted_at) return true;
  if (checkUserType === UserType.Customer) return !!user.customer?.deleted_at;
  if (
    checkUserType === UserType.Staff ||
    checkUserType === UserType.Instructor ||
    checkUserType === UserType.Admin ||
    checkUserType === UserType.SysAdmin
  )
    return !!user.staff?.deleted_at;
  return false;
};

export const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(amount);
};

// Financial-statement convention for a subtracted amount (e.g. a payroll
// deduction total) — parenthesized rather than a leading minus sign. Amounts
// this wraps are always stored as positive Ints (the sign is implied by
// which column/field it is, not by the number itself), so a zero deduction
// intentionally renders plain — there's nothing being subtracted to bracket.
export const formatDeduction = (amount: number) => (amount > 0 ? `(${formatCurrency(amount)})` : formatCurrency(amount));

// Accounting convention for a value that can itself be negative (e.g. THP
// once deductions exceed earnings) — parenthesizes the absolute value
// instead of a leading minus sign. Distinct from formatDeduction, which
// assumes a non-negative "amount subtracted" as input and always
// parenthesizes; this takes the actual signed total and only parenthesizes
// when it's actually negative, so a normal positive THP still renders plain.
export const formatSignedCurrency = (amount: number) =>
  amount < 0 ? `(${formatCurrency(Math.abs(amount))})` : formatCurrency(amount);

// The Upgrade column, shared by the on-screen tables and the PDF documents so
// the two can never disagree about when a dash appears. A dash means "no
// upgrades in this period", never "this product cannot be upgraded": the
// report must not assert what the catalogue does or does not allow, because
// that depends on tier structure, promo windows and the legacy class-set
// upgrade path, any of which can change under it. Excel says the same thing
// with an empty cell, which keeps that column numeric (see xlsx.ts).
// `?? 0` is deliberate: an admin deployed ahead of the server receives no
// `upgrades` field at all, and must still render a dash rather than
// "undefined".
export const formatUpgrades = (value?: number): string => {
  const count = value ?? 0;
  return count > 0 ? String(count) : "-";
};

// What the client actually handed over for a purchased package. Mirror of the
// server's cashCollected (util/analyticsShared.ts): an upgrade creates a new
// customer_package stamped with the NEW package's full catalogue price, but
// only the difference (upgrade_fee) ever changes hands. purchase_price stays
// the nominal value because the credit maths needs it, so every surface that
// answers "what was paid" has to come through here instead.
//
// `?? purchase_price` is also what makes the admin safe to deploy ahead of the
// server: with no upgrade_fee in the payload this renders exactly what it
// renders today.
export const amountPaid = (p: {
  purchase_price: number;
  upgrade_fee?: number | null;
}): number => p.upgrade_fee ?? p.purchase_price;

// A reward credit a client claimed from a loyalty or referral reward lives on
// a hidden system tier. It is a gift, not a sale: no payment, no transaction
// number, no invoice. Returns which programme gave it, or null for anything
// the studio sold.
export const rewardSourceOf = (packageDeal?: {
  system_kind?: string | null;
}): "loyalty" | "referral" | null => {
  switch (packageDeal?.system_kind) {
    case PackageSystemKind.LoyaltyReward:
      return "loyalty";
    case PackageSystemKind.ReferralReward:
      return "referral";
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// Status badges vs label chips (house rule, 2026-09-25)
//
// A STATUS is a state that changes over time (Confirmed, Expired, Draft...).
// Status badges keep their leading dot and read a fixed English, Title Case
// label from the maps below, identical in both languages (never translate()),
// so a status looks the same on every page, report and screenshot. Colours
// follow one rule set: green = active / completed / finished / configured,
// navy = in flight (confirmed, ongoing, issued, draft) or a neutral fact,
// amber = not yet or needs a hand (upcoming, scheduled, pending, reopened,
// not configured), grey = closed with nothing more to do (expired, ended,
// waitlist expired, no show, removed), red = a person switched it off
// (cancelled, refunded, inactive, deleted, voided), purple = waitlisted. Two
// sibling states may share a colour in one table; the word carries the
// difference.
//
// A LABEL says what a thing is (a role, a class type, Membership, Shared,
// Reward, a count). Label chips render without the dot (StatusBadge
// dot={false}) and stay localised like the page around them.
//
// The customer app and the instructor app mirror BOOKING_STATUS_LABELS and
// BOOKING_STATUS_TONE by hand (terasku-client src/components/pills.tsx,
// terasku-instructor src/shared/status.ts + components/pills.tsx): change
// them here and copy the change there. The server's Excel export mirrors
// DISCOUNT_CODE_STATUS_LABELS (services/exports/report-specs.ts).
// ---------------------------------------------------------------------------

// The six badge tones. The {bg, text} colour pairs live next to the badge
// component (pages/schedule/component/ScheduleStatusBadge.tsx).
export type StatusTone = "done" | "progress" | "navy" | "neutral" | "error" | "purple";

export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  [BookingStatus.Pending]: "Pending",
  [BookingStatus.Confirmed]: "Confirmed",
  [BookingStatus.Completed]: "Completed",
  [BookingStatus.Cancelled]: "Cancelled",
  [BookingStatus.NoShow]: "No Show",
  [BookingStatus.Refunded]: "Refunded",
  [BookingStatus.Waitlist]: "Waitlisted",
  [BookingStatus.WaitlistExpired]: "Waitlist Expired",
};

// On a booking row the colour says what happened to the credit: green =
// used (Completed), red = returned by the studio (Cancelled before the class,
// Refunded after it), grey = seat closed with nothing owed (No Show, Waitlist
// Expired), navy / amber / purple = still in flight.
export const BOOKING_STATUS_TONE: Record<BookingStatus, StatusTone> = {
  [BookingStatus.Pending]: "progress",
  [BookingStatus.Confirmed]: "navy",
  [BookingStatus.Completed]: "done",
  [BookingStatus.Cancelled]: "error",
  [BookingStatus.NoShow]: "neutral",
  [BookingStatus.Refunded]: "error",
  [BookingStatus.Waitlist]: "purple",
  [BookingStatus.WaitlistExpired]: "neutral",
};

export const getBookingStatusLabel = (status: string): string =>
  BOOKING_STATUS_LABELS[status as BookingStatus] ?? status;

export const SCHEDULE_TIME_STATUS_LABELS: Record<ScheduleTimeStatus, string> = {
  [ScheduleTimeStatus.Upcoming]: "Upcoming",
  [ScheduleTimeStatus.Ongoing]: "Ongoing",
  [ScheduleTimeStatus.Finished]: "Finished",
};

export const SCHEDULE_TIME_STATUS_TONE: Record<ScheduleTimeStatus, StatusTone> = {
  [ScheduleTimeStatus.Upcoming]: "progress",
  [ScheduleTimeStatus.Ongoing]: "navy",
  [ScheduleTimeStatus.Finished]: "done",
};

// A deleted schedule isn't a ScheduleTimeStatus value but shares the status
// tag. V2 has no cancel state: Hapus Jadwal writes a delete tombstone, which
// the API still reports as `is_cancelled` (a V1 field name), so the tag
// names the action the admin took.
export const SCHEDULE_DELETED_LABEL = "Deleted";
export const SCHEDULE_DELETED_TONE: StatusTone = "error";

// A package or membership tier deleted from the catalogue, on the product
// report rows that still list it for its sales or usage (Performa,
// Pendapatan, Bandingkan). Discontinued and grey, not a deleted schedule's
// red "Deleted": the studio no longer sells it and its history stays, which
// is a past fact rather than a problem. Never on a single sale's row
// (Pembelian): whether the product is still sold says nothing about the sale.
export const PRODUCT_DISCONTINUED_LABEL = "Discontinued";
// A booking on a goodwill credit (past the package's paid pool, Rp 0 revenue).
// One fixed word in both languages, like the other label chips.
export const GOODWILL_BOOKING_LABEL = "Bonus";
export const PRODUCT_DISCONTINUED_TONE: StatusTone = "neutral";

export const getScheduleTimeStatusLabel = (
  status: ScheduleTimeStatus,
): string => SCHEDULE_TIME_STATUS_LABELS[status] ?? status;

// Time-derived, like ScheduleTimeStatus (see TimeOffPage.tsx's displayStatus):
// LeaveStatus only ever stores Approved/Cancelled; "Upcoming"/"Ongoing"/
// "Completed" are derived from start_date/end_date vs now (a leave spans a
// date range the same way a schedule spans start_time/end_time).
export type LeaveDisplayStatus = "upcoming" | "ongoing" | "completed" | "cancelled";

export const LEAVE_STATUS_LABELS: Record<LeaveDisplayStatus, string> = {
  upcoming: "Upcoming",
  ongoing: "Ongoing",
  completed: "Completed",
  cancelled: "Cancelled",
};

export const LEAVE_STATUS_TONE: Record<LeaveDisplayStatus, StatusTone> = {
  upcoming: "progress",
  ongoing: "navy",
  completed: "done",
  cancelled: "error",
};

export const getLeaveStatusLabel = (status: LeaveDisplayStatus): string =>
  LEAVE_STATUS_LABELS[status];

// Klien / Karyawan / Akun Admin account status (per-role deactivation).
export type AccountStatus = "active" | "inactive";
export const ACCOUNT_STATUS_LABELS: Record<AccountStatus, string> = {
  active: "Active",
  inactive: "Inactive",
};
export const ACCOUNT_STATUS_TONE: Record<AccountStatus, StatusTone> = {
  active: "done",
  inactive: "error",
};

// A customer package's validity (the tag on the package card and the
// before / after words in its history). Same words as the account status
// today, kept as its own map so the two can diverge without a refactor.
export type PackageValidity = "active" | "inactive";
export const PACKAGE_VALIDITY_LABELS: Record<PackageValidity, string> = {
  active: "Active",
  inactive: "Inactive",
};
export const PACKAGE_VALIDITY_TONE: Record<PackageValidity, StatusTone> = {
  active: "done",
  inactive: "error",
};

// Why a customer package stopped (the Paket Tidak Aktif list). Keyed by the
// raw InactiveReason values.
export const INACTIVE_REASON_LABELS: Record<InactiveReason, string> = {
  [InactiveReason.NO_MORE_SESSIONS]: "Out of Credits",
  [InactiveReason.EXPIRED]: "Expired",
  [InactiveReason.UPGRADED]: "Upgraded",
  // A voided sale, and the loyalty free-credit claw-back (a void of a Rp 0
  // grant). A DELETED SCHEDULE keeps its own word "Deleted"; these are
  // different things.
  [InactiveReason.MANUALLY_MARKED_INVALID]: "Voided",
  [InactiveReason.ACCESS_REMOVED]: "Removed",
};
export const INACTIVE_REASON_TONE: Record<InactiveReason, StatusTone> = {
  [InactiveReason.NO_MORE_SESSIONS]: "navy",
  [InactiveReason.EXPIRED]: "neutral",
  [InactiveReason.UPGRADED]: "done",
  [InactiveReason.MANUALLY_MARKED_INVALID]: "error",
  [InactiveReason.ACCESS_REMOVED]: "neutral",
};

// Promo lifecycle of a package product. `promo_active` / `promo_ended` come
// from the server; PromoStatusBadge only picks the word and tone.
export type PromoStatus = "active" | "upcoming" | "ended";
export const PROMO_STATUS_LABELS: Record<PromoStatus, string> = {
  active: "Active",
  upcoming: "Upcoming",
  ended: "Ended",
};
export const PROMO_STATUS_TONE: Record<PromoStatus, StatusTone> = {
  active: "done",
  upcoming: "progress",
  ended: "neutral",
};
// The promo chip reads "Promo · Until 23 Aug" / "Promo · Starts 20 Aug" /
// "Promo · Ended 19 Jul" in both languages (date formatted with the "en"
// locale by the badge).
export const PROMO_CHIP_WORDS = {
  chip: "Promo",
  until: "Until",
  starts: "Starts",
  ended: "Ended",
} as const;

// Analytics rows (PackageAnalyticsRow) carry is_promo / promo_active and the
// two dates but no promo_ended, so "not active" alone would call a promo that
// has not opened yet "Ended". Derive upcoming from the start date instead, so
// the report chip agrees with the catalogue chip for the same product. It
// lives here with the maps rather than beside the badge component because the
// report PDFs read it too.
export const promoStatusOfDates = (row: {
  is_promo: boolean;
  promo_active: boolean;
  promo_starts_at: string | null;
}): PromoStatus | null => {
  if (!row.is_promo) return null;
  if (row.promo_active) return "active";
  if (row.promo_starts_at && now().isBefore(row.promo_starts_at)) return "upcoming";
  return "ended";
};

// What a report's promo chip says: "Promo · Active", "Promo · Upcoming",
// "Promo · Ended". All three states name themselves, so a reader never has to
// work out whether a bare "Promo" means running. The dates stay in the report's
// own Promo Period column rather than being repeated here; the product
// catalogue, which has no such column, prints the deadline in its chip
// instead (see PromoStatusBadge). One source for the screen chip and the PDF
// cell, fixed English on both sides like every status word.
export const promoChipLabel = (status: PromoStatus): string =>
  `${PROMO_CHIP_WORDS.chip} · ${PROMO_STATUS_LABELS[status]}`;

// The ONE chip a product report shows beside a product's name: a promo's
// state ("Promo · Active" / "Upcoming" / "Ended"), "Discontinued" for a tier
// deleted from the catalogue, and "Promo · Discontinued" when it was a promo.
// One chip rather than "Promo · Ended" beside "Discontinued": both say it is
// no longer sold, and Ended is wrong for a promo deleted mid-campaign (the
// server never counts a deleted promo as running). Null for a regular product
// still on sale. One source for the screen chip and the PDF's line under the
// name.
export const productStatusChip = (row: {
  is_promo: boolean;
  promo_active: boolean;
  promo_starts_at: string | null;
  is_deleted?: boolean;
}): { label: string; tone: StatusTone } | null => {
  if (row.is_deleted) {
    return {
      label: row.is_promo
        ? `${PROMO_CHIP_WORDS.chip} · ${PRODUCT_DISCONTINUED_LABEL}`
        : PRODUCT_DISCONTINUED_LABEL,
      tone: PRODUCT_DISCONTINUED_TONE,
    };
  }
  const status = promoStatusOfDates(row);
  return status ? { label: promoChipLabel(status), tone: PROMO_STATUS_TONE[status] } : null;
};

// Discount code effective status (derived server-side; only active/disabled
// are stored). Key order is load-bearing: the Kode Diskon table builds its
// filter dropdown from Object.keys of the tone map.
export const DISCOUNT_CODE_STATUS_LABELS: Record<DiscountCodeEffectiveStatus, string> = {
  active: "Active",
  scheduled: "Scheduled",
  expired: "Expired",
  disabled: "Inactive",
};
export const DISCOUNT_CODE_STATUS_TONE: Record<DiscountCodeEffectiveStatus, StatusTone> = {
  active: "done",
  scheduled: "progress",
  expired: "neutral",
  disabled: "error",
};

// Loyalty / referral voucher: the stored issued / redeemed / voided plus the
// derived "expired". "Voided" is kept on purpose (the issuer invalidated a
// document); "Cancelled" already means a booking or class was called off.
export type VoucherEffectiveStatus = VoucherStatus | "expired";
export const VOUCHER_STATUS_LABELS: Record<VoucherEffectiveStatus, string> = {
  [VoucherStatus.Issued]: "Issued",
  [VoucherStatus.Redeemed]: "Redeemed",
  [VoucherStatus.Voided]: "Voided",
  expired: "Expired",
};
export const VOUCHER_STATUS_TONE: Record<VoucherEffectiveStatus, StatusTone> = {
  [VoucherStatus.Issued]: "navy",
  [VoucherStatus.Redeemed]: "done",
  [VoucherStatus.Voided]: "error",
  expired: "neutral",
};

// A loyalty stamp in Riwayat Stamp: counting toward the card, voided, or
// still counting on a completed card although its class is no longer
// attended (removed only if a reward of that card is voided and the card
// reopens).
export type StampStatus = "counted" | "voided" | "pending_revert";
export const STAMP_STATUS_LABELS: Record<StampStatus, string> = {
  counted: "Counted",
  voided: "Voided",
  pending_revert: "Pending revert",
};
export const STAMP_STATUS_TONE: Record<StampStatus, StatusTone> = {
  counted: "done",
  voided: "error",
  pending_revert: "progress",
};

// A loyalty card in the Loyalty Card tab's card selector. "new" is the next
// card, not started yet, and reads as the current one.
export const LOYALTY_CARD_STATUS_LABELS: Record<
  "current" | "new" | "completed" | "expired" | "ended",
  string
> = {
  current: "Current",
  new: "Current",
  completed: "Completed",
  expired: "Expired",
  ended: "Ended",
};
export const LOYALTY_CARD_STATUS_TONE: Record<
  "current" | "new" | "completed" | "expired" | "ended",
  StatusTone
> = {
  current: "navy",
  new: "navy",
  completed: "done",
  expired: "neutral",
  ended: "neutral",
};

// Referral attribution: stored pending / converted plus the derived "lapsed"
// (a pending referral past its deadline), which reads as Expired like every
// other deadline that passed.
export const REFERRAL_STATUS_LABELS: Record<ReferralEffectiveStatus, string> = {
  [ReferralStatus.Pending]: "Pending",
  [ReferralStatus.Converted]: "Converted",
  lapsed: "Expired",
};
export const REFERRAL_STATUS_TONE: Record<ReferralEffectiveStatus, StatusTone> = {
  [ReferralStatus.Pending]: "progress",
  [ReferralStatus.Converted]: "done",
  lapsed: "neutral",
};

export const PAYROLL_RUN_STATUS_LABELS: Record<PayrollRunStatus, string> = {
  [PayrollRunStatus.Draft]: "Draft",
  [PayrollRunStatus.Reopened]: "Reopened",
  [PayrollRunStatus.Finalized]: "Finalized",
};
export const PAYROLL_RUN_STATUS_TONE: Record<PayrollRunStatus, StatusTone> = {
  [PayrollRunStatus.Draft]: "navy",
  [PayrollRunStatus.Reopened]: "progress",
  [PayrollRunStatus.Finalized]: "done",
};

// Komisi per Kelas: whether a class has a complete commission rate.
export type PayrollRateStatus = "configured" | "unconfigured";
export const PAYROLL_RATE_STATUS_LABELS: Record<PayrollRateStatus, string> = {
  configured: "Configured",
  unconfigured: "Not Configured",
};
export const PAYROLL_RATE_STATUS_TONE: Record<PayrollRateStatus, StatusTone> = {
  configured: "done",
  unconfigured: "progress",
};

// Pembatasan Admin: the pill beside each capability switch (and the before /
// after words in its change log).
export type AdminRestrictionState = "restricted" | "open";
export const ADMIN_RESTRICTION_LABELS: Record<AdminRestrictionState, string> = {
  restricted: "Restricted",
  open: "Open",
};
export const ADMIN_RESTRICTION_TONE: Record<AdminRestrictionState, StatusTone> = {
  restricted: "navy",
  open: "neutral",
};

// Loyalty Card / Referral program status pill (only ever shown while active;
// an off program shows its setup note instead).
export const PROGRAM_STATUS_LABELS = { active: "Active" } as const;
export const PROGRAM_STATUS_TONE: StatusTone = "done";

// ---------------------------------------------------------------------------
// LABEL chips. Same rule as the status maps above: one word per thing, fixed
// English Title Case, identical in both languages, so a chip reads the same on
// every page, report, PDF and export. Only the chip is fixed; the sentences,
// headings, buttons and table headers around it stay localised, and roles
// printed as PROSE keep their i18n keys (karyawan_type.*, the payroll Tipe
// column, page headings). Label chips render without the dot.
// ---------------------------------------------------------------------------

// Role chips on a profile, Akun Admin and Mulai Proses Penggajian. Every role
// chip is navy, however many a person holds. `customer` reads "Client", the
// same word title.client uses in English.
export const USER_ROLE_LABELS: Record<UserType, string> = {
  [UserType.SysAdmin]: "System Admin",
  [UserType.Manager]: "Manager",
  [UserType.Admin]: "Admin",
  [UserType.Staff]: "Staff",
  [UserType.Instructor]: "Instructor",
  [UserType.Customer]: "Client",
};

// Paket vs Membership, on the Transaksi tab's type chip and every Membership
// pill in the package pickers.
export const PRODUCT_TYPE_LABELS = { package: "Package", membership: "Membership" } as const;

export const productTypeLabel = (unlimited: boolean): string =>
  unlimited ? PRODUCT_TYPE_LABELS.membership : PRODUCT_TYPE_LABELS.package;

// Kelas yang Ditugaskan chip on the Instruktur list: every class type, a
// count, or none yet.
export const ASSIGNED_CLASSES_LABELS = { all: "All", none: "None" } as const;

export const assignedClassesLabel = (count: number): string =>
  `${count} ${count === 1 ? "Class" : "Classes"}`;

// A package shared with other clients, and a package that came from a loyalty
// or referral reward. Both already read the same word in either language; they
// live here so a future translation edit cannot silently split them.
export const SHARED_LABEL = "Shared";
export const REWARD_LABEL = "Reward";

// A package created BY an upgrade. A label (what this row is), never a dot,
// and deliberately not the old package's status word "Upgraded": one word
// must not carry two looks. Read by the package card, the Transaksi tab and
// the Pembelian report so all three cannot drift.
export const UPGRADE_LABEL = "Upgrade";

// Mirrors of terasku-server's src/util/customer-package-void.ts constants,
// kept in sync by convention (same as shared/utils/tax.ts). The void reason
// Cancel Upgrade stamps on the new half of an upgrade (a string, never a
// VoidReason member, so the void dialog can never offer it), and the
// status-log reason the upgrade writes on the old half when it retires it.
export const UPGRADE_CANCELLED_VOID_REASON = "upgrade_cancelled";
export const UPGRADED_STATUS_REASON = "upgraded";

// A voided sale, wherever it is kept in a list: struck through and muted, so
// it is plainly still on the record and plainly not counted.
export const VOIDED_TEXT: CSSProperties = {
  textDecoration: "line-through",
  color: colors.ink3,
};

// Kelola Berbagi Paket: the owner chip beside the buyer's name, and the
// used/total seats chip.
export const PACKAGE_OWNER_LABEL = "Owner";

export const sharingSlotsLabel = (used: number, max: number): string =>
  `${used} of ${max} People`;

// Calendar day chip: how many schedules were actioned that day. The chip
// renders the number and the word in separate spans (the word hides in a
// narrow day column), so the word has its own helper. Unlike the label chips
// above, this word is localised (Andrew, 2026-10-03): "Aksi" in Indonesian,
// "Action(s)" in English, matching the "Aksi" the calendar pages use elsewhere.
export const actionCountWord = (count: number): string =>
  translate(
    count === 1
      ? "schedule.actionable_bookings.chip_single"
      : "schedule.actionable_bookings.chip_many"
  );

export const actionCountLabel = (count: number): string => `${count} ${actionCountWord(count)}`;

// Notification drawer: unseen count beside the heading.
export const unseenCountLabel = (count: number): string => `${count} New`;

// True when the booking HOLDS A SEAT on its occurrence — the client-side
// mirror of the server's SEAT_CONSUMING_STATUSES (booking-v2-occurrence.ts).
// Drives the "x/y Booking" chips, the class-full → waitlist gate, and the
// undo seat guards. Cancelled/no-show/waitlist/waitlist-expired have freed
// (or never took) the seat; everything else (confirmed/completed/refunded
// and any pre-v2 stragglers) occupies one. Refunded deliberately keeps its
// seat: a goodwill refund returns the credit but never resells the slot.
export const isSeatHoldingBookingStatus = (status: string): boolean =>
  status !== BookingStatus.Cancelled &&
  status !== BookingStatus.NoShow &&
  status !== BookingStatus.Waitlist &&
  status !== BookingStatus.WaitlistExpired;

// True for rows that belong on the Waitlist tab — live and snapshot/expired.
export const isWaitlistStatus = (status: string): boolean =>
  status === BookingStatus.Waitlist ||
  status === BookingStatus.WaitlistExpired;

// True once the schedule's start_time has passed (inclusive). Drives:
//   - waitlist closure (add/reorder blocked, lazy cleanup runs)
//   - PrimaryAction tooltip "Kelas sudah dimulai"
export const hasScheduleStarted = (
  schedule: { start_time: string | Date },
): boolean => !now().isBefore(tzDate(schedule.start_time));

export const getExpiryDateBadgeStatus = (expiryDate: Dayjs): "error" | "warning" | "success" => {
  if (isCustomerPackageExpired(expiryDate)) {
    return "error";
  } else if (expiryDate.isBefore(now().startOf("day").add(1, "week"))) {
    return "warning";
  } else {
    return "success";
  }
};

// The expiry date is inclusive
// Hence, during the expiry date, the customer package is still usable
export const isCustomerPackageExpired = (expiryDate?: string | Date | Dayjs): boolean => {
  const actualExpiryDate = tzDate(expiryDate).endOf("day");
  return now().isAfter(actualExpiryDate);
};

export const getActualRemainingCredits = (customerPackage: CustomerPackage): number => {
  if (customerPackage.package_deal.unlimited) return Infinity;

  const remainingCredits = customerPackage.remaining_credits > 0 ? customerPackage.remaining_credits : 0;
  const onholdCredits = customerPackage.onhold_credits > 0 ? customerPackage.onhold_credits : 0;
  return remainingCredits - onholdCredits;
};

// Booking pickers: " · 2 kredit" after a package whose tier charges more
// than one credit for the picked class (credit_cost, sent by the picker
// endpoints). Nothing at 1, nothing on a membership.
export const creditCostSuffix = (customerPackage: CustomerPackage): string => {
  const cost = customerPackage.credit_cost ?? 1;
  return customerPackage.package_deal.unlimited || cost === 1
    ? ""
    : ` · ${translateWithVars("customer_package.credit_cost_each", { n: cost })}`;
};

// Who can use a shared package right now. `max_members` counts the OWNER,
// who is not a member row, hence the 1 +. Fall back to 2, not 1: a shareable
// package with no configured cap would otherwise read "1 of 1" and refuse to
// add anyone. Shared by the package card's caption and the sharing modal's
// badge so the two can never disagree.
export const packageSharingCapacity = (cp: {
  members?: { deleted_at?: string | Date | null }[] | null;
  package_deal: { max_members?: number | null };
}) => {
  const activeMembers = (cp.members ?? []).filter((m) => !m.deleted_at);
  const max = cp.package_deal.max_members ?? 2;
  const used = 1 + activeMembers.length;
  return { used, max, isFull: used >= max };
};

export const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Validity-start state of a sold package (Pengaturan -> Masa Berlaku Produk).
// "Belum aktif" = a first-use package with no class yet, no hand-set expiry
// (activation_locked) and an auto-start date still ahead: its clock has not
// started, so surfaces show "-" for the expiry (the stored expiry_date is
// only the ceiling the auto-start would give it, kept so every sorter and
// eligibility check keeps working). `durationLabel` is the tier validity
// ("30 Hari") the client gets once the clock starts. Mirrors the server's
// isUnactivatedFirstUse.
type PackageValiditySource = {
  validity_value?: number | null;
  validity_unit?: string | null;
  package_deal: {
    expiry_value?: number | null;
    expiry_unit?: string | null;
    expiry_duration_by_days: number;
  };
};

// The validity length a package was SOLD with (its own snapshot), falling
// back to the tier for rows that carry none. Mirrors the server's
// validityOf(), which every first-use recompute and window check reads, so a
// tier edited after the sale never changes what these surfaces show or allow.
export const packageValidity = (cp: PackageValiditySource) =>
  cp.validity_value != null
    ? {
        expiry_value: cp.validity_value,
        expiry_unit: cp.validity_unit ?? "day",
        expiry_duration_by_days: cp.validity_value,
      }
    : cp.package_deal;

export const packageActivation = (
  cp: PackageValiditySource & {
    validity_start?: string | null;
    activated_at?: string | Date | null;
    activation_locked?: boolean | null;
    auto_start_date?: string | Date | null;
  },
) => {
  const firstUse = cp.validity_start === ValidityStart.FirstUse;
  const autoStart = cp.auto_start_date ? new Date(cp.auto_start_date) : null;
  return {
    firstUse,
    unactivated:
      firstUse &&
      !cp.activated_at &&
      !cp.activation_locked &&
      !!autoStart &&
      autoStart.getTime() > Date.now(),
    autoStartDate: autoStart,
    durationLabel: formatTierValidity(packageValidity(cp)),
  };
};

// "30 Hari" / "2 Bulan" / "1 Tahun" for a catalog tier ("1 Month" / "3
// Months" in English: the count picks the singular or plural word, where the
// `expiry_unit.*` dictionary the unit pickers use is plural only). Falls back
// to the legacy day count for ungrouped packages that predate
// expiry_value/unit.
export const formatTierValidity = (tier: {
  expiry_value?: number | null;
  expiry_unit?: string | null;
  expiry_duration_by_days: number;
}): string => {
  const value = tier.expiry_value ?? tier.expiry_duration_by_days;
  const unit = tier.expiry_unit ?? "day";
  return `${value} ${translate(`expiry_unit_count.${unit}_${value === 1 ? "one" : "other"}`)}`;
};

// A class's commission structure as one label: "Per Kelas (Nominal Tetap)",
// "Per Peserta (Rata)", "Per Pendapatan (Persentase)". Shared by the Komisi
// per Kelas list and the payroll run's session breakdown. Revenue-based
// structures are per_attendee on the server with a revenue rate_structure,
// so the mode label is derived from the structure, not the stored mode.
export const commissionStructureLabel = (mode: string | null, rateStructure: string | null): string => {
  if (!mode || !rateStructure) return "-";
  if (mode === CommissionMode.PerClass) return translate("payrollPage.rates.mode_per_class");
  if (
    rateStructure === CommissionRateStructure.RevenuePercent ||
    rateStructure === CommissionRateStructure.RevenueTiered ||
    rateStructure === CommissionRateStructure.RevenuePercentTiered
  ) {
    const revenueSubLabel =
      rateStructure === CommissionRateStructure.RevenueTiered
        ? translate("payrollPage.rates.structure_tiered")
        : rateStructure === CommissionRateStructure.RevenuePercentTiered
          ? translate("payrollPage.rates.structure_revenue_percent_tiered")
          : translate("payrollPage.rates.structure_revenue_percent");
    return `${translate("payrollPage.rates.mode_revenue")} (${revenueSubLabel})`;
  }
  const subLabel =
    rateStructure === CommissionRateStructure.Tiered
      ? translate("payrollPage.rates.structure_tiered")
      : translate("payrollPage.rates.structure_flat");
  return `${translate("payrollPage.rates.mode_per_attendee")} (${subLabel})`;
};
